import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Loader2, RefreshCw } from 'lucide-react';
import Modal from '../../shared/Modal';
import {
  useInfluencerCheckout,
  SUBSCRIPTION_STATUS,
} from '../context/InfluencerCheckoutContext';
import { useInfluencerSubscriptionPolling } from '../hooks/useInfluencerSubscriptionPolling';
import { getUserProfile } from '../../../Redux/userSlice';
import InfluencerBillingForm from './InfluencerBillingForm';
import InfluencerPaymentButton from './InfluencerPaymentButton';

const InfluencerCheckoutModal = () => {
  const dispatch = useDispatch();
  const { open, closeCheckout, plan, subscriptionStatus, setSubscriptionStatus } =
    useInfluencerCheckout();

  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [errors, setErrors] = useState({});

  const handleSubscriptionActivated = useCallback(() => {
    closeCheckout();
    setSubscriptionStatus(SUBSCRIPTION_STATUS.IDLE);
    dispatch(getUserProfile());
    toast.success('Insta Tools Premium activated successfully.');
  }, [closeCheckout, dispatch, setSubscriptionStatus]);

  const handleSubscriptionTimeout = useCallback(() => {
    setSubscriptionStatus(SUBSCRIPTION_STATUS.TIMEOUT);
  }, [setSubscriptionStatus]);

  useInfluencerSubscriptionPolling({
    enabled: open && subscriptionStatus === SUBSCRIPTION_STATUS.PROCESSING,
    onActivated: handleSubscriptionActivated,
    onTimeout: handleSubscriptionTimeout,
  });

  useEffect(() => {
    if (!open) {
      setFormData({ name: '', email: '', mobile: '' });
      setErrors({});
    }
  }, [open]);

  if (!open || !plan) return null;

  const isProcessing = subscriptionStatus === SUBSCRIPTION_STATUS.PROCESSING;
  const isTimeout = subscriptionStatus === SUBSCRIPTION_STATUS.TIMEOUT;

  if (isProcessing) {
    return (
      <Modal onClose={() => {}} size="xl">
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <Loader2 className="w-12 h-12 text-black animate-spin mb-4" aria-hidden />
          <h2 className="text-xl font-semibold mb-2">Processing your subscription...</h2>
          <p className="text-gray-600">
            We are activating Insta Tools Premium. This usually takes a few seconds.
          </p>
        </div>
      </Modal>
    );
  }

  if (isTimeout) {
    return (
      <Modal onClose={closeCheckout} size="xl">
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <p className="text-gray-700 mb-6">
            Payment processing is taking longer than expected. Refresh to see your updated plan.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            type="button"
            onClick={closeCheckout}
            className="mt-4 text-gray-600 hover:text-gray-800 underline text-sm"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={closeCheckout} size="xl">
      <h2 className="text-xl font-semibold mb-6">Upgrade to {plan.name}</h2>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="md:w-1/2">
          <InfluencerBillingForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        </div>

        <div className="md:w-1/2 space-y-6">
          <div className="rounded-xl border p-4 bg-gray-50">
            <p className="text-sm text-gray-600">Plan</p>
            <p className="text-lg font-semibold">{plan.name}</p>
            <p className="text-2xl font-bold mt-2">
              {plan.currencySymbol}
              {plan.price}
              <span className="text-sm font-normal text-gray-600">/month</span>
            </p>
            {plan.description && (
              <p className="text-sm text-gray-600 mt-3">{plan.description}</p>
            )}
          </div>

          <div className="rounded-xl border p-4">
            <InfluencerPaymentButton
              plan={plan}
              formData={formData}
              setErrors={setErrors}
            />
            <p className="text-xs text-center text-gray-500 mt-3">
              Secure payment via Razorpay · Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InfluencerCheckoutModal;
